import { useCallback, useMemo, useRef, useState } from 'react';
import '../referrals.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Toolbar, Item as ItemT, SearchPanel, Paging, StateStoring, Scrolling, Lookup } from 'devextreme-react/data-grid';
import RemisionesAPI from '../../../api/api.remmisions';
import { Item } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Button, Tooltip } from 'devextreme-react';
import { newToast } from '../../../components';
import { useAppSelector } from '../../../contexts/hooks';
import { ConvertLocalDateTime } from '../../../utils/functions';

interface TemporalRemissionsProps {
  onClose: () => void;
}

// Texto legible para una diferencia de tiempo en milisegundos
const formatDistance = (ms: number) => {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${Math.max(minutes, 1)} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;

  const days = Math.round(hours / 24);
  return `${days} día${days === 1 ? '' : 's'}`;
};

export const TemporalRemissions = ({ onClose }: TemporalRemissionsProps) => {
  const dataRef = useRef<DataGrid>(null);
  const { getTemporalRemisions, CreateTemporalRemision, SetTemporalRemision, DeleteTemporalRemision } = RemisionesAPI();
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  // Fecha de inicio vigente en el formulario de edición (incluye cambios sin guardar),
  // usada como mínimo del editor de fecha fin
  const editingStartDateRef = useRef<Date | null>(null);

  // Al cambiar la fecha de inicio se refrescan los editores dependientes y se arrastra
  // la fecha fin si quedó por debajo del nuevo inicio
  const setStartDateCellValue = useCallback((newData: any, value: any, currentRowData: any) => {
    newData.Rmt_FechaInicio = value;
    editingStartDateRef.current = value ? new Date(value) : null;

    const currentEnd = currentRowData?.Rmt_FechaFin;
    if (value && currentEnd && new Date(currentEnd) < new Date(value)) {
      newData.Rmt_FechaFin = value;
    }
  }, []);

  // Obtener laboratorios desde Redux - SOLO para selectores del formulario de creación/edición
  const laboratorios = useAppSelector((state) => state.configuration.Data?.LabReferencia);

  // Obtener estudios desde Redux - SOLO para selectores del formulario de creación/edición
  const estudios = useAppSelector((state) => state.configuration.Data?.Estudios);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Rmt_Id',
      async load() {
        const data = await getTemporalRemisions();
        return Promise.resolve({
          data: data || [],
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        // Se envía la hora literal: como Date, JSON la serializaría en UTC (+5)
        const payload = {
          ...values,
          Rmt_FechaInicio: ConvertLocalDateTime(values.Rmt_FechaInicio),
          Rmt_FechaFin: ConvertLocalDateTime(values.Rmt_FechaFin),
        };

        return CreateTemporalRemision(payload)
          .then((res) => {
            if (res === 105) {
              dataRef.current?.instance.refresh();
            }
            return res;
          })
          .catch(() => {
            throw new Error('Error insertando la remisión temporal');
          });
      },
      update: (key, values) => {
        return new Promise((resolve, reject) => {
          dataRef.current?.instance
            .byKey(key)
            .then((originalRecord) => {
              // Mezclar el registro original con los valores modificados
              const merged = { ...originalRecord, ...values };

              // Enviar SOLO los campos escalares de la tabla. Si se incluye el objeto
              // de navegación "Laboratorio"/"Estudio" (que queda con el valor VIEJO al
              // hacer el merge), EF sobrescribe Rmt_Lr_Id con Laboratorio.Lr_Id y el
              // laboratorio nunca cambia al editar.
              const payload = {
                Rmt_Id: merged.Rmt_Id,
                Rmt_Es_Id: merged.Rmt_Es_Id,
                Rmt_Lr_Id: merged.Rmt_Lr_Id,
                // Se envía la hora literal: como Date, JSON la serializaría en UTC (+5)
                Rmt_FechaInicio: ConvertLocalDateTime(merged.Rmt_FechaInicio),
                Rmt_FechaFin: ConvertLocalDateTime(merged.Rmt_FechaFin),
                Rmt_Observaciones: merged.Rmt_Observaciones ?? null,
                Rmt_Estado: merged.Rmt_Estado,
              };

              console.log('[SetTemporalRemision] payload enviado =', payload);

              SetTemporalRemision(payload)
                .then((res) => {
                  if (res === 107) {
                    dataRef.current?.instance.refresh();
                  }
                  resolve(res);
                })
                .catch((error) => {
                  reject(new Error('Error actualizando la remisión temporal'));
                });
            })
            .catch(reject);
        });
      },
      remove: (key) => {
        return DeleteTemporalRemision(key)
          .then(() => {
            dataRef.current?.instance.refresh();
          })
          .catch(() => {
            throw new Error('Error eliminando la remisión temporal');
          });
      },
    });
  }, []);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleRefresh = useCallback(() => {
    dataRef.current?.instance.refresh();
    newToast('SUCCESS', 'Datos actualizados', 5);
  }, []);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Es_Id', rowData.Rmt_Es_Id);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Lr_Id', rowData.Rmt_Lr_Id);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_FechaInicio', rowData.Rmt_FechaInicio);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_FechaFin', rowData.Rmt_FechaFin);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Observaciones', rowData.Rmt_Observaciones ? `${rowData.Rmt_Observaciones} - Copia` : null);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Estado', true);
      }
    }, 100);

    newToast('INFO', 'Remisión temporal duplicada. Complete la edición para guardar.', 3);
  }, []);

  // Vigencia de la remisión respecto al momento actual
  const getStatus = useCallback((data: any) => {
    if (data?.Rmt_Estado === false) {
      return { label: 'INACTIVA', color: '#6c757d', bg: '#f1f3f5', detail: 'La remisión está desactivada y no se aplica' };
    }

    const now = new Date();
    const start = data?.Rmt_FechaInicio ? new Date(data.Rmt_FechaInicio) : null;
    const end = data?.Rmt_FechaFin ? new Date(data.Rmt_FechaFin) : null;

    if (start && now < start) {
      return { label: 'PRÓXIMA', color: '#ef6c00', bg: '#fff3e0', detail: `Inicia en ${formatDistance(start.getTime() - now.getTime())}` };
    }
    if (end && now > end) {
      return { label: 'VENCIDA', color: '#c62828', bg: '#ffebee', detail: `Terminó hace ${formatDistance(now.getTime() - end.getTime())}` };
    }
    return { label: 'EN CURSO', color: '#2e7d32', bg: '#e8f5e9', detail: end ? `Termina en ${formatDistance(end.getTime() - now.getTime())}` : 'Sin fecha de fin' };
  }, []);

  const renderStatus = useCallback(
    (cellData: any) => {
      const status = getStatus(cellData.data);
      const badgeId = `rmt-status-${cellData.data?.Rmt_Id ?? cellData.rowIndex}`;

      return (
        <>
          <span
            id={badgeId}
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.3px',
              color: status.color,
              backgroundColor: status.bg,
              border: `1px solid ${status.color}33`,
            }}>
            {status.label}
          </span>
          <Tooltip target={`#${badgeId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
            <div className='tooltip-content'>{status.detail}</div>
          </Tooltip>
        </>
      );
    },
    [getStatus]
  );

  // Renderizador personalizado para laboratorio - Usa datos del backend
  const renderLaboratorio = useCallback((cellData: any) => {
    const laboratorio = cellData.data?.Laboratorio;
    return <span>{laboratorio?.Lr_Nombre || 'No asignado'}</span>;
  }, []);

  // Renderizador personalizado para estudio - Usa datos del backend
  const renderEstudio = useCallback((cellData: any) => {
    const estudio = cellData.data?.Estudio;
    return (
      <div>
        <strong>{estudio?.Es_Codigo || ''}</strong> - {estudio?.Es_Nombre || 'No asignado'}
      </div>
    );
  }, []);

  return (
    <div style={{ height: '600px', width: '100%' }}>
      <DataGrid
        dataSource={customDataSrc}
        ref={dataRef}
        allowColumnReordering={true}
        height={'100%'}
        width={'100%'}
        columnAutoWidth={false}
        allowColumnResizing={true}
        showBorders={true}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        onSelectionChanged={(e: any) => setSelectedRowKeys(e.selectedRowKeys)}
        onEditingStart={(e) => {
          editingStartDateRef.current = e.data?.Rmt_FechaInicio ? new Date(e.data.Rmt_FechaInicio) : null;
        }}
        onInitNewRow={(e) => {
          editingStartDateRef.current = e.data?.Rmt_FechaInicio ? new Date(e.data.Rmt_FechaInicio) : null;
        }}
        onEditorPreparing={(e) => {
          if (e.parentType === 'dataRow' && e.dataField === 'Rmt_FechaFin') {
            const start = editingStartDateRef.current ?? (e.row?.data?.Rmt_FechaInicio ? new Date(e.row.data.Rmt_FechaInicio) : null);
            if (start) {
              e.editorOptions.min = start;
            }
          }
        }}
        onRowValidating={(e) => {
          const start = e.newData?.Rmt_FechaInicio ?? e.oldData?.Rmt_FechaInicio;
          const end = e.newData?.Rmt_FechaFin ?? e.oldData?.Rmt_FechaFin;
          if (start && end && new Date(end) < new Date(start)) {
            e.isValid = false;
            e.errorText = 'La fecha de fin no puede ser menor que la fecha de inicio';
          }
        }}>
        <StateStoring enabled={true} type='localStorage' storageKey='temporal-remissions-grid-state' />
        <SearchPanel visible={true} width={300} placeholder='Buscar remisiones temporales...' />
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />

        <Editing mode='form' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} useIcons={true}>
          <Form labelLocation='top' colCount={2}>
            <Item
              dataField='Rmt_Es_Id'
              colSpan={2}
              editorType='dxSelectBox'
              editorOptions={{
                dataSource: estudios,
                displayExpr: (item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : ''),
                valueExpr: 'Es_Id',
                searchEnabled: true,
                placeholder: 'Seleccione un estudio',
                stylingMode: 'outlined',
              }}>
              <RequiredRule message='El estudio es requerido' />
            </Item>
            <Item
              dataField='Rmt_Lr_Id'
              colSpan={2}
              editorType='dxSelectBox'
              editorOptions={{
                dataSource: laboratorios,
                displayExpr: 'Lr_Nombre',
                valueExpr: 'Lr_Id',
                searchEnabled: true,
                placeholder: 'Seleccione un laboratorio',
                stylingMode: 'outlined',
              }}>
              <RequiredRule message='El laboratorio es requerido' />
            </Item>
            <Item
              dataField='Rmt_FechaInicio'
              colSpan={1}
              editorType='dxDateBox'
              editorOptions={{
                stylingMode: 'outlined',
                type: 'datetime',
                displayFormat: 'dd/MM/yyyy HH:mm',
              }}>
              <RequiredRule message='La fecha de inicio es requerida' />
            </Item>
            <Item
              dataField='Rmt_FechaFin'
              colSpan={1}
              editorType='dxDateBox'
              editorOptions={{
                stylingMode: 'outlined',
                type: 'datetime',
                displayFormat: 'dd/MM/yyyy HH:mm',
              }}>
              <RequiredRule message='La fecha de fin es requerida' />
            </Item>
            <Item
              dataField='Rmt_Observaciones'
              editorType='dxTextArea'
              colSpan={2}
              editorOptions={{
                height: 100,
                placeholder: 'Ingrese observaciones...',
                stylingMode: 'outlined',
              }}
            />
            <Item
              dataField='Rmt_Estado'
              editorType='dxCheckBox'
              colSpan={2}
              editorOptions={{
                text: 'Activo',
              }}
            />
          </Form>
        </Editing>

        <Toolbar>
          <ItemT name='searchPanel' />
          <ItemT name='addRowButton' />
          <ItemT location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefreshTemporalRemissions' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefreshTemporalRemissions'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </ItemT>
        </Toolbar>

        <Column allowGrouping={false} caption='ID' visible={true} allowEditing={false} dataField='Rmt_Id' alignment='center' width={80} sortOrder='desc' />

        <Column allowGrouping={false} caption='ESTUDIO' visible={true} allowEditing={true} dataField='Rmt_Es_Id' alignment='left' minWidth={250}>
          <RequiredRule />
          <Lookup dataSource={estudios} displayExpr={(item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : '')} valueExpr='Es_Id' />
        </Column>

        <Column allowGrouping={false} caption='LABORATORIO' visible={true} allowEditing={true} dataField='Rmt_Lr_Id' alignment='center' width={200}>
          <RequiredRule />
          <Lookup dataSource={laboratorios} displayExpr='Lr_Nombre' valueExpr='Lr_Id' />
        </Column>

        <Column allowGrouping={false} caption='FECHA INICIO' visible={true} allowEditing={true} dataField='Rmt_FechaInicio' alignment='center' width={170} dataType='datetime' format='dd/MM/yyyy HH:mm' setCellValue={setStartDateCellValue}>
          <RequiredRule />
        </Column>

        <Column allowGrouping={false} caption='FECHA FIN' visible={true} allowEditing={true} dataField='Rmt_FechaFin' alignment='center' width={170} dataType='datetime' format='dd/MM/yyyy HH:mm'>
          <RequiredRule />
        </Column>

        <Column allowGrouping={false} caption='OBSERVACIONES' visible={true} allowEditing={true} dataField='Rmt_Observaciones' alignment='left' minWidth={200} />

        <Column dataType='boolean' allowGrouping={false} caption='ESTADO' visible={true} allowEditing={true} dataField='Rmt_Estado' alignment='center' width={80}>
          <RequiredRule />
        </Column>

        {/* Columna calculada: permite buscar y agrupar por vigencia además de verla */}
        <Column caption='VIGENCIA' name='Vigencia' allowEditing={false} allowGrouping={true} alignment='center' width={120} calculateCellValue={(rowData: any) => getStatus(rowData).label} cellRender={renderStatus} />

        <Column type='buttons' caption={'ACCIÓN'} width={110}>
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar remisión temporal' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
